import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { classNames } from "primereact/utils";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useUsers from "../hooks/useUsers";
import { UserCreateBody } from "../api/models/user.models";

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

const MainLayoutContainer = styled.div`
  min-height: 100vh;
  min-width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--sumi-background-blue);

  a {
    color: var(--text-color);
    text-decoration: underline;
    cursor: pointer;
  }

  h1 {
    font-size: 36px;
    color: var(--text-color);
  }

  & label {
    font-size: 12px;
  }
`;

const Form = styled.div`
  width: 90%;
  max-width: 500px;
  padding: 3rem;
  background-color: var(--blue-200);
  color: var(--text-color);
  border-radius: 5px;
`;

const InputContainer = styled.div`
  width: 100%;
  height: 105px;
  text-align: left;

  & .p-inputtext {
    height: 56px;
  }

  & div,
  .p-float-label,
  .p-inputtext,
  .p-password,
  .p-button {
    width: 100%;
  }
`;

const CreateAccountContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  text-align: center;
  color: var(--text-color);

  & a {
    margin-left: 0.5rem;
    cursor: pointer;
  }
`;

export const CreateNewAccount = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  const { createNewUser, savingNewUser } = useUsers({
    enableUsers: false,
    companyId: null,
    organizationId: null,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: formData,
  });

  const handleCreate = (data: Inputs) => {
    // login here
    const payload: UserCreateBody = {
      id: 0,
      names: {
        firstName: data.firstName,
        lastName: data.lastName,
        name: `${data.firstName} ${data.lastName}`,
      },
      email: {
        email: data.email,
        isValidated: true,
      },
      phoneNumber: {
        number: "+358404444444",
        isValidated: true,
      },
      password: data.password,
    };
    createNewUser({ ...payload, companyId: 0, organizationId: 0 });
  };

  const getFormErrorMessage = (name: string) => {
    return errors[name] ? (
      <small className="p-error">{errors[name].message}</small>
    ) : (
      <></>
    );
  };

  return (
    <MainLayoutContainer className="login-screen">
      <h1>{t("header-create-new-account")}</h1>
      <Form>
        <form onSubmit={handleSubmit(handleCreate)}>
          <div>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: t("warning-required") }}
              render={({ field, fieldState }) => (
                <InputContainer>
                  <label htmlFor={field.name}>{t("label-first-name")}</label>
                  <InputText
                    id={field.name}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setFormData({ ...formData, firstName: e.target.value });
                    }}
                    className={classNames({ "p-invalid": fieldState.error })}
                    placeholder={t("label-first-name")}
                  />
                  {getFormErrorMessage(field.name)}
                </InputContainer>
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{ required: t("warning-required") }}
              render={({ field, fieldState }) => (
                <InputContainer>
                  <label htmlFor={field.name}>{t("label-last-name")}</label>
                  <InputText
                    id={field.name}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setFormData({ ...formData, lastName: e.target.value });
                    }}
                    className={classNames({ "p-invalid": fieldState.error })}
                    placeholder={t("label-last-name")}
                  />
                  {getFormErrorMessage(field.name)}
                </InputContainer>
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{
                required: t("warning-required"),
                pattern: {
                  value:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: t("warning-invalid-email"),
                },
              }}
              render={({ field, fieldState }) => (
                <InputContainer>
                  <label htmlFor={field.name}>{t("label-email")}</label>
                  <InputText
                    id={field.name}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setFormData({ ...formData, email: e.target.value });
                    }}
                    className={classNames({ "p-invalid": fieldState.error })}
                    placeholder={t("label-email")}
                  />
                  {getFormErrorMessage(field.name)}
                </InputContainer>
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{ required: t("warning-required") }}
              render={({ field, fieldState }) => (
                <InputContainer>
                  <label htmlFor={field.name}>{t("label-password")}</label>
                  <Password
                    id={field.name}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setFormData({ ...formData, password: e.target.value });
                    }}
                    className={classNames({ "p-invalid": fieldState.error })}
                    feedback={false}
                    toggleMask
                    pt={{
                      showIcon: { style: { verticalAlign: "unset" } },
                      hideIcon: { style: { verticalAlign: "unset" } },
                    }}
                    placeholder={t("label-password")}
                  />
                  {getFormErrorMessage(field.name)}
                </InputContainer>
              )}
            />

            <InputContainer style={{ height: "75px" }}>
              <Button
                id="create-button"
                type="submit"
                label={t("cmd-create-account")}
                loading={false}
                style={{ height: "50px", marginTop: "1rem" }}
              />
            </InputContainer>
          </div>
        </form>
      </Form>

      <CreateAccountContainer>
        <a href="/login">{t("label-back-to-login")}</a>
      </CreateAccountContainer>
    </MainLayoutContainer>
  );
};
